import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  History,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  GitCompare,
  FileText,
  Loader2,
  Check,
  X,
  Plus,
  Minus
} from 'lucide-react';

const VersionHistory = ({ contractId, onVersionRestore, onContentChange }) => {
  const { api } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [compareVersions, setCompareVersions] = useState({ a: null, b: null });
  const [comparisonResult, setComparisonResult] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [contractId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contracts/${contractId}/versions`);
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Error fetching versions:', error);
      // Might not have any versions yet
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (versionNumber) => {
    if (!window.confirm(`¿Restaurar el contrato a la versión ${versionNumber}? Esto creará una nueva versión con el contenido anterior.`)) {
      return;
    }
    
    setRestoring(true);
    try {
      const response = await api.post(`/contracts/${contractId}/versions/restore`, {
        version_number: versionNumber
      });
      
      toast.success(response.data.message);
      await fetchVersions();
      
      if (onVersionRestore) {
        onVersionRestore(response.data);
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      toast.error('Error al restaurar la versión');
    } finally {
      setRestoring(false);
    }
  };

  const handleCompare = async () => {
    if (!compareVersions.a || !compareVersions.b) {
      toast.error('Selecciona dos versiones para comparar');
      return;
    }
    
    setComparing(true);
    try {
      const response = await api.get(
        `/contracts/${contractId}/versions/compare?version_a=${compareVersions.a}&version_b=${compareVersions.b}`
      );
      setComparisonResult(response.data);
    } catch (error) {
      console.error('Error comparing versions:', error);
      toast.error('Error al comparar versiones');
    } finally {
      setComparing(false);
    }
  };

  const toggleVersionSelect = (versionNumber) => {
    if (compareVersions.a === versionNumber) {
      setCompareVersions({ ...compareVersions, a: null });
    } else if (compareVersions.b === versionNumber) {
      setCompareVersions({ ...compareVersions, b: null });
    } else if (!compareVersions.a) {
      setCompareVersions({ ...compareVersions, a: versionNumber });
    } else if (!compareVersions.b) {
      setCompareVersions({ ...compareVersions, b: versionNumber });
    } else {
      // Replace the older selection
      setCompareVersions({ a: compareVersions.b, b: versionNumber });
    }
    setComparisonResult(null);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No hay historial de versiones</p>
        <p className="text-sm mt-1">Las versiones se crean automáticamente al editar el contrato</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="version-history">
      {/* Header with compare button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <History className="w-5 h-5 text-blue-400" />
          Historial de Versiones
          <span className="text-sm text-slate-400 font-normal">({versions.length})</span>
        </h3>
        
        {compareVersions.a && compareVersions.b && (
          <button
            onClick={handleCompare}
            disabled={comparing}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-500 disabled:opacity-50"
          >
            {comparing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <GitCompare className="w-4 h-4" />
            )}
            Comparar v{compareVersions.a} vs v{compareVersions.b}
          </button>
        )}
      </div>

      {/* Comparison Result */}
      {comparisonResult && (
        <div className="bg-slate-800/50 border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Comparación de Versiones</h4>
            <button 
              onClick={() => setComparisonResult(null)}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
              <Plus className="w-5 h-5 mx-auto text-green-400 mb-1" />
              <div className="text-green-400 font-medium">{comparisonResult.diff?.additions || 0}</div>
              <div className="text-slate-400">Adiciones</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <Minus className="w-5 h-5 mx-auto text-red-400 mb-1" />
              <div className="text-red-400 font-medium">{comparisonResult.diff?.deletions || 0}</div>
              <div className="text-slate-400">Eliminaciones</div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <FileText className="w-5 h-5 mx-auto text-blue-400 mb-1" />
              <div className="text-blue-400 font-medium">{comparisonResult.diff?.total_changes || 0}</div>
              <div className="text-slate-400">Total</div>
            </div>
          </div>
          
          {comparisonResult.diff?.diff_text && (
            <div className="bg-slate-900 rounded-lg p-3 max-h-60 overflow-y-auto">
              <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono">
                {comparisonResult.diff.diff_text.split('\n').map((line, i) => (
                  <div 
                    key={i}
                    className={
                      line.startsWith('+') && !line.startsWith('+++') ? 'text-green-400 bg-green-500/10' :
                      line.startsWith('-') && !line.startsWith('---') ? 'text-red-400 bg-red-500/10' :
                      line.startsWith('@') ? 'text-blue-400' :
                      ''
                    }
                  >
                    {line}
                  </div>
                ))}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Version List */}
      <div className="space-y-2">
        {versions.map((version, index) => {
          const isSelected = compareVersions.a === version.version_number || 
                            compareVersions.b === version.version_number;
          const isExpanded = expandedVersion === version.version_number;
          const isLatest = index === 0;
          
          return (
            <div 
              key={version.id}
              className={`bg-slate-800/50 border rounded-lg transition-all ${
                isSelected ? 'border-blue-500' : 'border-border'
              }`}
            >
              <div className="p-3 flex items-center gap-3">
                {/* Selection checkbox for comparison */}
                <button
                  onClick={() => toggleVersionSelect(version.version_number)}
                  className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                    isSelected 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'border-slate-600 hover:border-slate-400'
                  }`}
                  title="Seleccionar para comparar"
                >
                  {isSelected && <Check className="w-3 h-3" />}
                </button>
                
                {/* Version info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Versión {version.version_number}</span>
                    {isLatest && (
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                        Actual
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 truncate">
                    {version.change_description}
                  </p>
                </div>
                
                {/* Metadata */}
                <div className="text-right text-sm hidden sm:block">
                  <div className="flex items-center gap-1 text-slate-400">
                    <Clock className="w-3 h-3" />
                    {formatDate(version.created_at)}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1">
                  {!isLatest && (
                    <button
                      onClick={() => handleRestore(version.version_number)}
                      disabled={restoring}
                      className="p-1.5 text-slate-400 hover:text-amber-400 rounded transition-colors"
                      title="Restaurar esta versión"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setExpandedVersion(isExpanded ? null : version.version_number)}
                    className="p-1.5 text-slate-400 hover:text-white rounded transition-colors"
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Expanded content */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 border-t border-border mt-2">
                  <div className="text-xs text-slate-500 mt-2 mb-2">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {formatDate(version.created_at)}
                  </div>
                  {version.diff_from_previous && (
                    <div className="flex gap-4 text-xs mb-2">
                      <span className="text-green-400">+{version.diff_from_previous.additions} líneas</span>
                      <span className="text-red-400">-{version.diff_from_previous.deletions} líneas</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VersionHistory;
